import React from 'react';
import { Copy, Info } from 'lucide-react';
import { PromptVariable } from '../../../types/prompt';

interface TemplateVariableListProps {
  variables: string[];
  availableVariables?: PromptVariable[];
  onCopyVariable: (variable: string) => void;
}

export default function TemplateVariableList({
  variables,
  availableVariables = [],
  onCopyVariable
}: TemplateVariableListProps) {
  const getVariableInfo = (varName: string): PromptVariable | undefined => {
    return availableVariables.find(v => v.name === varName);
  };

  const getSourceColor = (source: string): string => {
    const colors: Record<string, string> = {
      country: 'bg-green-100 text-green-700',
      category: 'bg-blue-100 text-blue-700',
      actor: 'bg-red-100 text-red-700',
      framework: 'bg-purple-100 text-purple-700',
      custom: 'bg-gray-100 text-gray-700',
    };
    return colors[source] || colors.custom;
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
        Template Variables
      </h4>
      
      {variables.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No variables found in template
        </p>
      ) : (
        <div className="space-y-2">
          {variables.map((variable) => {
            const info = getVariableInfo(variable);
            
            return (
              <div
                key={variable}
                className="flex items-start justify-between p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-mono text-blue-600 dark:text-blue-400">
                      {`{{${variable}}}`}
                    </code>
                    {info && (
                      <span className={`px-2 py-0.5 text-xs rounded ${getSourceColor(info.source)}`}>
                        {info.source}
                      </span>
                    )}
                  </div>
                  
                  {info && info.value && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                      {info.value.substring(0, 100)}{info.value.length > 100 && '...'}
                    </p>
                  )}
                </div>
                
                <button
                  onClick={() => onCopyVariable(`{{${variable}}}`)}
                  className="ml-2 p-1 text-gray-500 hover:text-blue-600 transition-colors"
                  title="Copy variable"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-gray-600 dark:text-gray-400 mt-0.5" />
          <div className="text-xs text-gray-700 dark:text-gray-300">
            <p className="font-medium mb-1">Available variable sources:</p>
            <ul className="space-y-0.5 ml-4">
              <li>• <strong>Country:</strong> Name, code, region</li>
              <li>• <strong>Category:</strong> Threat categories and signals</li>
              <li>• <strong>Actor:</strong> Primary threat actor</li>
              <li>• <strong>Framework:</strong> Analysis methodology</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}